import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { scrollIntoViewWithinContainer } from '../utils/scroll';

/** Отличение элемента, к которому привёл переход */
export const NAVIGATION_TARGET_HIGHLIGHT_CLASS = 'ring-2 ring-primary/50 bg-primary/5';

/** Наведение области на элемент, к которому ведёт переход */
export type NavigationTarget = {
  /** Ключ элемента, к которому ведёт переход; `null` — переход цели не называл */
  targetKey: string | null;
  /** Ref элемента-цели: по его появлению в разметке область прокручивается к нему */
  attachTarget: (element: HTMLElement | null) => void;
};

/**
 * Элемент, к которому ведёт переход, названный в адресе своим query-параметром: область
 * прокручивается к нему, а сам он отличается от соседей общим {@link NAVIGATION_TARGET_HIGHLIGHT_CLASS}.
 *
 * Элемент цели приходит ref-ом: содержимое области строит другой компонент, и к моменту, когда
 * цель оказывается в разметке, эффект по готовому списку уже отработал бы. Сама прокрутка идёт
 * следом отдельным проходом и по явно переданной области: полоса прокрутки включается не в том же
 * проходе, что и содержимое, а до этого область прокручиваемой не выглядит — прокрутка прямо из
 * ref-а не сдвинула бы ничего, и от перехода оставалась бы одна отметка.
 *
 * Ключ отдаётся строкой, как он стоит в адресе: сравнивает его с собственным ключом элемента сам
 * потребитель — у одних он составной (`block-12`), у других это номер. Неизвестная цель просто не
 * встречается среди элементов — переход остаётся обычным открытием.
 */
export const useNavigationTarget = (param: string, viewportRef: RefObject<HTMLDivElement | null>): NavigationTarget => {
  const [searchParams] = useSearchParams();
  const targetKey = searchParams.get(param);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const shownKeyRef = useRef<string | null>(null);

  const attachTarget = useCallback((element: HTMLElement | null) => {
    if (element) setTargetElement(element);
  }, []);

  useEffect(() => {
    if (!targetKey || !targetElement || shownKeyRef.current === targetKey) return;
    shownKeyRef.current = targetKey;
    scrollIntoViewWithinContainer(targetElement, {
      align: 'center',
      behavior: 'smooth',
      container: viewportRef.current,
    });
  }, [targetElement, targetKey, viewportRef]);

  return { targetKey, attachTarget };
};
